import TurndownService from "turndown";

const createTurndownService = () => {
  // Simple Turndown configuration
  const service = new TurndownService({
    headingStyle: "atx",
    bulletListMarker: "-",
    codeBlockStyle: "fenced",
    fence: "```",
    emDelimiter: "*",
    strongDelimiter: "**",
    linkStyle: "inlined"
  });

  // Remove underline support to avoid conversion issues
  // Underline will be stripped during HTML to markdown conversion
  service.addRule("removeUnderline", {
    filter: function (node) {
      return (
        node.nodeName === "U" ||
        (node.nodeName === "SPAN" &&
          node.style &&
          node.style.textDecoration === "underline")
      );
    },
    replacement: function (content) {
      return content; // Just return the content without underline tags
    }
  });
  return service;
};

const turndownService = createTurndownService();

// CommonMark also reads "1)" as a list marker; Turndown escapes only "1.".
const commonMarkTurndownService = createTurndownService();
commonMarkTurndownService.escape = (text: string) =>
  turndownService.escape(text).replace(/^(\d+)\)/, "$1\\)");

// Numbered lists right after one another alternate "." and ")": with one delimiter CommonMark reads them as one list.
const delimiterOf = (list: Element) => {
  let previous = list.previousElementSibling;
  let run = 0;
  while (previous?.nodeName === "OL") {
    run += 1;
    previous = previous.previousElementSibling;
  }
  return run % 2 ? ")" : ".";
};

// Turndown's own numbered item, with that delimiter.
commonMarkTurndownService.addRule("numberedItem", {
  filter: (node) =>
    node.nodeName === "LI" && node.parentNode?.nodeName === "OL",
  replacement: (content, node) => {
    const list = node.parentNode as Element;
    const start = Number(list.getAttribute("start") || 1);
    const index = Array.prototype.indexOf.call(list.children, node);
    const text = content
      .replace(/^\n+/, "")
      .replace(/\n+$/, "\n")
      .replace(/\n/gm, "\n    ");
    return `${start + index}${delimiterOf(list)}  ${text}${
      node.nextSibling && !/\n$/.test(text) ? "\n" : ""
    }`;
  }
});

// Zero-width space: the only "empty" content Turndown keeps through the
// HTML→markdown round-trip, so blank lines (empty paragraphs) survive it.
const BLANK_LINE_MARKER = String.fromCharCode(0x200b);

export interface HtmlToMarkdownOptions {
  // Keeps Turndown's escapes (plus "1)"), so typed text stays literal for CommonMark.
  keepEscapes?: boolean;
}

/**
 * Convert HTML content to markdown
 * @param html - HTML string to convert
 * @returns Markdown string
 */
export const htmlToMarkdown = (
  html: string,
  { keepEscapes = false }: HtmlToMarkdownOptions = {}
): string => {
  if (!html || typeof html !== "string" || html.trim() === "") {
    return "";
  }

  try {
    // Encode empty paragraphs (intentional blank lines) so they aren't dropped.
    const cleanHtml = html.replace(
      /<p>\s*<\/p>/g,
      `<p>${BLANK_LINE_MARKER}</p>`
    );

    const markdown = (
      keepEscapes ? commonMarkTurndownService : turndownService
    ).turndown(cleanHtml);

    const trimmed = markdown
      .replace(/\n{3,}/g, "\n\n") // Replace 3+ newlines with just 2
      .replace(/^\n+|\n+$/g, ""); // Only trim newlines from start and end, preserve other whitespace
    const unescaped = keepEscapes
      ? trimmed
      : trimmed
          .replace(/\\\*/g, "*") // Unescape asterisks
          .replace(/\\\[/g, "[") // Unescape brackets
          .replace(/\\\]/g, "]"); // Unescape brackets

    // Enhanced cleanup of the resulting markdown for better list handling
    const finalMarkdown = unescaped
      // Fix malformed list transitions
      .replace(/(\n\d+\.\s[^\n]*)\n\n(\d+\.\s)/g, "$1\n$2") // Fix ordered list continuity
      .replace(/(\n[-*+]\s[^\n]*)\n\n([-*+]\s)/g, "$1\n$2") // Fix unordered list continuity
      // Clean up mixed list transitions
      .replace(/(\n\d+\.\s[^\n]*)\n\n([-*+]\s)/g, "$1\n\n$2") // Ensure proper spacing between different list types
      .replace(/(\n[-*+]\s[^\n]*)\n\n(\d+\.\s)/g, "$1\n\n$2"); // Ensure proper spacing between different list types

    // A document that is only blank-line markers / whitespace is effectively
    // empty: return "" so the editor reports empty (placeholder shows, no
    // stray zero-width content that would defeat isEmpty checks).
    if (finalMarkdown.split(BLANK_LINE_MARKER).join("").trim() === "") {
      return "";
    }

    return finalMarkdown || "";
  } catch (error) {
    console.error("Error converting HTML to markdown:", error);
    return html || "";
  }
};

/**
 * Convert markdown content to HTML for display
 * This is a simple converter for basic markdown elements
 * @param markdown - Markdown string to convert
 * @returns HTML string
 */
export const markdownToHtml = (markdown: string): string => {
  if (!markdown || typeof markdown !== "string" || markdown.trim() === "") {
    return "";
  }

  try {
    let html = markdown;

    // Handle escaped markdown characters
    html = html.replace(/\\#/g, "#");
    html = html.replace(/\\\*/g, "*");
    html = html.replace(/\\\[/g, "[");
    html = html.replace(/\\\]/g, "]");

    // Convert headings
    html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");
    html = html.replace(/^## (.*$)/gim, "<h2>$1</h2>");
    html = html.replace(/^# (.*$)/gim, "<h1>$1</h1>");

    // Convert bold and italic
    html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "<em>$1</em>");

    // Convert angle-bracket URLs (e.g., <https://example.com>) to clickable links
    // This must be done before other link processing to prevent HTML tag interpretation
    html = html.replace(/<(https?:\/\/[^>]+)>/g, '<a href="$1">$1</a>');

    // Convert links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

    // Simple list processing
    const lines = html.split("\n");
    const processedLines: string[] = [];
    let inOrderedList = false;
    let inUnorderedList = false;

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Skip empty lines within lists
      if (!trimmedLine) {
        if (!inOrderedList && !inUnorderedList) {
          processedLines.push(line);
        }
        continue;
      }

      // Check for ordered list items (must start with "1." to begin a new list)
      const orderedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
      if (orderedMatch && orderedMatch[2].trim()) {
        const number = parseInt(orderedMatch[1]);

        if (!inOrderedList && number !== 1) {
          // Don't start a list with numbers other than 1
          processedLines.push(line);
          continue;
        }

        if (!inOrderedList) {
          if (inUnorderedList) {
            processedLines.push("</ul>");
            inUnorderedList = false;
          }
          processedLines.push("<ol>");
          inOrderedList = true;
        }
        processedLines.push(`<li>${orderedMatch[2]}</li>`);
        continue;
      }

      // Check for unordered list items
      const unorderedMatch = trimmedLine.match(/^[-*+]\s+(.+)$/);
      if (unorderedMatch && unorderedMatch[1].trim()) {
        if (!inUnorderedList) {
          if (inOrderedList) {
            processedLines.push("</ol>");
            inOrderedList = false;
          }
          processedLines.push("<ul>");
          inUnorderedList = true;
        }
        processedLines.push(`<li>${unorderedMatch[1]}</li>`);
        continue;
      }

      // Close lists on non-list content
      if (inOrderedList) {
        processedLines.push("</ol>");
        inOrderedList = false;
      }
      if (inUnorderedList) {
        processedLines.push("</ul>");
        inUnorderedList = false;
      }
      processedLines.push(line);
    }

    // Close any remaining open lists
    if (inOrderedList) processedLines.push("</ol>");
    if (inUnorderedList) processedLines.push("</ul>");

    // Join and make list HTML compact (prevents TipTap from creating empty list items)
    html = processedLines
      .join("\n")
      .replace(/<(ol|ul)>\n/g, "<$1>")
      .replace(/\n<\/(ol|ul)>/g, "</$1>")
      .replace(/<\/li>\n<li>/g, "</li><li>");

    // Handle paragraphs - preserve line breaks and spaces
    const paragraphs = html.split(/\n\s*\n/);
    html = paragraphs
      .map((para) => {
        // Only trim completely empty paragraphs, preserve spaces in content
        if (!para.trim()) return "";

        // Don't wrap headings, lists, or HTML tags
        if (para.match(/^<(h[1-6]|ul|ol|li)/i)) {
          return para;
        }

        // Convert single newlines to <br> within paragraphs, preserving spaces
        // Trim only the very start and end, but preserve internal spacing
        const trimmedPara = para.replace(/^\s+|\s+$/g, "");
        return `<p>${trimmedPara.replace(/\n/g, "<br>")}</p>`;
      })
      .filter((p) => p)
      .join("");

    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/g, "");
    html = html.replace(/<p>\s*<\/p>/g, "");

    return html;
  } catch (error) {
    console.error("Error converting markdown to HTML:", error);
    return `<p>${markdown.replace(/\n/g, "<br>")}</p>`;
  }
};

/**
 * Detect if content is HTML or markdown
 * @param content - Content to analyze
 * @returns true if content appears to be HTML, false if markdown
 */
export const isHtmlContent = (content: string): boolean => {
  if (!content || typeof content !== "string" || content.trim() === "") {
    return false;
  }

  // Simple heuristic: if it contains HTML tags, it's probably HTML
  const htmlTagRegex = /<[^>]+>/;
  return htmlTagRegex.test(content);
};

/**
 * Process editor content for saving - converts HTML to markdown
 * @param html - HTML content from TipTap editor
 * @returns Markdown string for safe storage
 */
export const processEditorContentForSave = (
  html: string,
  options?: HtmlToMarkdownOptions
): string => {
  return htmlToMarkdown(html || "", options);
};

/**
 * Process stored content for loading into editor
 * @param content - Stored content (could be HTML or markdown)
 * @returns Content ready for TipTap editor
 */
export const processStoredContentForEditor = (content: string): string => {
  if (!content || typeof content !== "string" || content.trim() === "") {
    return "";
  }

  // Convert escaped newlines (\n as literal string) to actual newlines
  // This handles cases where the backend sends escaped newline characters
  const processedContent = content.replace(/\\n/g, "\n");

  // If it's already HTML, return as is
  if (isHtmlContent(processedContent)) {
    return processedContent;
  }

  // If it's markdown, convert to HTML for the editor
  return markdownToHtml(processedContent);
};
